import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
    Box,
    Typography,
    LinearProgress,
    Chip,
    Button,
    useTheme,
    alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { getPlanDisplayName } from '../../utils/planUtils';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';

const StyledLinearProgress = styled(LinearProgress)(({ theme, percentage }) => {
    let color = theme.palette.success.main;
    if (percentage >= 90) {
        color = theme.palette.error.main;
    } else if (percentage >= 75) {
        color = theme.palette.warning.main;
    } else if (percentage >= 50) {
        color = '#ff9800';
    }

    return {
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.08)
            : theme.palette.grey[100],
        '& .MuiLinearProgress-bar': {
            borderRadius: 5,
            background: percentage >= 90
                ? `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.error.light})`
                : percentage >= 75
                    ? `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`
                    : percentage >= 50
                        ? `linear-gradient(90deg, #ff9800, #ffb74d)`
                        : `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
        },
    };
});

const UsageLimitDisplay = ({
    currentUsage,
    limit,
    percentage,
    remaining,
    planType,
    showUpgradeButton = true
}) => {
    const navigate = useNavigate();
    const theme = useTheme();

    const getStatusColor = (pct) => {
        if (pct >= 90) return 'error';
        if (pct >= 75) return 'warning';
        if (pct >= 50) return 'warning';
        return 'success';
    };

    const getStatusText = (pct) => {
        if (pct >= 100) return 'Limit Reached';
        if (pct >= 90) return 'Critical';
        if (pct >= 75) return 'High Usage';
        if (pct >= 50) return 'Moderate';
        return 'Good';
    };

    const handleUpgradeClick = () => {
        navigate('/AccountSettings?tab=plan-information');
    };

    const formatPlanName = (plan) => {
        if (!plan) return 'Beta';
        return getPlanDisplayName(plan);
    };

    const { user } = useAuth();

    return (
        <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible'
        }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h3" component="div" sx={{
                    fontWeight: 800,
                    mb: 0.5,
                    lineHeight: 1.1,
                }}>
                    {currentUsage?.toLocaleString() || 0}
                    <Typography component="span" variant="h5" color="text.secondary" sx={{ fontWeight: 400 }}>
                        {' / '}{limit?.toLocaleString() || 0}
                    </Typography>
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                    <Chip
                        label={getStatusText(percentage)}
                        color={getStatusColor(percentage)}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
                    />
                    <Chip
                        label={formatPlanName(planType)}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
                    />
                </Box>
            </Box>

            <StyledLinearProgress
                variant="determinate"
                value={Math.min(percentage, 100)}
                percentage={percentage}
                sx={{ mb: 1.5 }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mb: 1.5 }}>
                {remaining > 0
                    ? `${remaining.toLocaleString()} chats remaining this month`
                    : `${Math.abs(remaining).toLocaleString()} chats over limit`
                }
            </Typography>

            {user?.owner && (
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={handleUpgradeClick}
                    fullWidth
                    startIcon={<RocketLaunchRoundedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 0.75,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        '&:hover': {
                            borderColor: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                        }
                    }}
                >
                    Need More? Upgrade Plan
                </Button>
            )}
        </Box>
    );
};

export default UsageLimitDisplay;
